import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Button } from './ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card'
import { Input } from './ui/input'
import { Badge } from './ui/badge'
import Navigation from './Navigation'
import { 
  ArrowRight, 
  Play, 
  Download, 
  Users, 
  Star, 
  CheckCircle, 
  Zap,
  Target,
  TrendingUp,
  Shield,
  Clock,
  Award
} from 'lucide-react'
import { motion } from 'framer-motion'

const LandingPage = () => {
  const [email, setEmail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleEmailSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)
    
    // Simulate email capture
    setTimeout(() => {
      setIsSubmitting(false)
      setEmail('')
      // You could integrate with an email service here
      alert('Thank you for your interest! We\'ll be in touch soon.')
    }, 1000)
  }

  const features = [
    {
      icon: Play,
      title: 'Exclusive Training Sessions',
      description: 'Access premium video content from industry leaders and accelerate your business growth.'
    },
    {
      icon: Download,
      title: 'Downloadable Resources',
      description: 'Get templates, guides, and tools that you can use immediately in your business.'
    },
    {
      icon: Users,
      title: 'Community Access',
      description: 'Connect with like-minded entrepreneurs and build valuable relationships.'
    },
    {
      icon: Target,
      title: 'Focused Learning',
      description: 'Curated content designed specifically for business acceleration and growth.'
    }
  ]

  const benefits = [
    'Unlimited access to all training videos',
    'Monthly live Q&A sessions',
    'Downloadable business templates',
    'Private community forum',
    'Mobile-friendly platform',
    'Cancel anytime'
  ]

  const testimonials = [
    {
      name: 'Sarah Johnson',
      role: 'CEO, TechStart',
      content: 'The strategies I learned here helped me scale my business from $100K to $1M in just 18 months.',
      rating: 5
    },
    {
      name: 'Michael Chen',
      role: 'Founder, GrowthCo',
      content: 'Incredible value and actionable insights. The community alone is worth the membership.',
      rating: 5
    },
    {
      name: 'Emily Rodriguez',
      role: 'Business Coach',
      content: 'I\'ve recommended this to all my clients. The ROI is undeniable.',
      rating: 5
    }
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      {/* Navigation */}
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 px-4">
        <div className="container mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-8"
          >
            <img 
              src="/IAS-C-logo-blue.png" 
              alt="Impact Accelerator Circle" 
              className="w-25 h-25 mx-auto mb-6 logo-container"
              style={{ width: '100px', height: 'auto' }}
            />
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Accelerate Your
              <span className="text-gradient block">Business Impact</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8 leading-relaxed">
              Join the Impact Accelerator Circle and gain access to exclusive training, resources, and a community of entrepreneurs committed to creating meaningful change.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link to="/register">
                <Button size="lg" className="text-lg px-8 py-4 rounded-2xl">
                  Get Started Today
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/login">
                <Button variant="outline" size="lg" className="text-lg px-8 py-4 rounded-2xl">
                  Sign In
                </Button>
              </Link>
            </div>
          </motion.div>

          {/* Email Capture */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="max-w-md mx-auto"
          >
            <form onSubmit={handleEmailSubmit} className="flex gap-3">
              <Input
                type="email"
                placeholder="Enter your email for updates"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="rounded-2xl h-12"
                required
              />
              <Button 
                type="submit" 
                disabled={isSubmitting}
                className="rounded-2xl h-12 px-6"
              >
                {isSubmitting ? 'Submitting...' : 'Subscribe'}
              </Button>
            </form>
            <p className="text-sm text-gray-500 mt-2">
              Get notified about new features and exclusive content
            </p>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-4">
        <div className="container mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Everything You Need to Succeed
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Our comprehensive platform provides the tools, knowledge, and community support to accelerate your business growth.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="text-center p-6 rounded-3xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-0 bg-white">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <feature.icon className="w-8 h-8 text-white" />
                  </div>
                  <CardTitle className="text-xl mb-3">{feature.title}</CardTitle>
                  <CardDescription className="text-gray-600 leading-relaxed">
                    {feature.description}
                  </CardDescription>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 px-4 bg-white/50">
        <div className="container mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Why Choose Impact Accelerator Circle?
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Join thousands of entrepreneurs who have transformed their businesses with our proven approach.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="flex items-center space-x-3"
              >
                <div className="w-8 h-8 bg-green-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-5 h-5 text-white" />
                </div>
                <span className="text-gray-700 font-medium">{benefit}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 px-4">
        <div className="container mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              What Our Members Say
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Real results from real entrepreneurs who have transformed their businesses.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="p-6 rounded-3xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-0 bg-white">
                  <div className="flex items-center mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <CardDescription className="text-gray-600 mb-4 italic leading-relaxed">
                    "{testimonial.content}"
                  </CardDescription>
                  <div>
                    <p className="font-semibold text-gray-900">{testimonial.name}</p>
                    <p className="text-sm text-gray-500">{testimonial.role}</p>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-gradient-to-r from-blue-600 to-blue-700">
        <div className="container mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold text-white mb-4">
              Ready to Accelerate Your Impact?
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              Join thousands of entrepreneurs who are already creating meaningful change in their communities.
            </p>
            <Link to="/register">
              <Button size="lg" variant="outline" className="text-lg px-8 py-4 rounded-2xl bg-white text-blue-600 hover:bg-blue-50 border-white">
                Start Your Journey Today
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 bg-gray-900">
        <div className="container mx-auto text-center">
          <img 
            src="/IAS-C-logo-blue.png" 
            alt="Impact Accelerator Circle" 
            className="w-25 h-25 mx-auto mb-6 logo-container"
            style={{ width: '100px', height: 'auto' }}
          />
          <h3 className="text-2xl font-bold text-white mb-4">Impact Accelerator Circle</h3>
          <p className="text-gray-400 mb-6 max-w-md mx-auto">
            Empowering entrepreneurs to create meaningful impact through education, resources, and community.
          </p>
          <div className="flex justify-center space-x-6 mb-6">
            <Link to="/login" className="text-gray-400 hover:text-white transition-colors">
              Sign In
            </Link>
            <Link to="/register" className="text-gray-400 hover:text-white transition-colors">
              Get Started
            </Link>
            {/* TODO: Re-enable when bug report backend is implemented
            <Link to="/report-bug" className="text-gray-400 hover:text-white transition-colors">
              Report Bug
            </Link>
            */}
          </div>
          <p className="text-gray-500 text-sm">
            © 2025 Impact Accelerator Circle. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  )
}

export default LandingPage

